/* eslint-disable sonarjs/cognitive-complexity */
import { useIntl } from 'react-intl';
import moment from 'moment';

import { getDaysInYearsAndMonths } from '@/features/battery/utils/DateUtils';
import { ExternalHistoricalBatteryHealthResponse, SystemLevelBatteryStatuses } from '@/generated/GeneratedApiTypes';
import { BatteryHistoryGraphTooltipKpi } from './BatteryHistoryGraphTooltipKpi';

type BatteryHistoryGraphTooltipProps = {
    active?: boolean;
    payload?: any;
    batteryType?: ExternalHistoricalBatteryHealthResponse['battery_type'];
};

export const BatteryHistoryGraphTooltip = ({ active, payload, batteryType }: BatteryHistoryGraphTooltipProps) => {
    const intl = useIntl();

    if (!active) {
        return null;
    }

    if (active && payload?.length && payload[0].payload) {
        const {
            state_of_health_percentage,
            state_of_health_status,
            mileage,
            full_charge_energy,
            usage_days,
            discharging_energy_throughput,
            depth_of_discharge_percentage,
            charged_energy_fast_charge,
            timestamp,
        } = payload[0].payload as SystemLevelBatteryStatuses;

        const isCbpVehicle = batteryType?.includes('CBP');

        return (
            <div
                className="border border-width-1 border-color-light bg-white padding-x-10 padding-y-10"
                data-testid="historical-graph-tooltip"
            >
                <div>{moment(timestamp).format('D MMMM YYYY')}</div>
                <hr className="margin-y-5" />
                <BatteryHistoryGraphTooltipKpi
                    i18nId="intl-msg:batteryService.stateOfHealth"
                    value={state_of_health_percentage}
                    unit="%"
                    additionalContent={
                        <span className="text-medium">
                            (
                            {intl.formatMessage({
                                id: `intl-msg:batteryService.batteryStatus.${state_of_health_status}`,
                            })}
                            )
                        </span>
                    }
                />
                <BatteryHistoryGraphTooltipKpi
                    i18nId="intl-msg:batteryService.totalMileage"
                    value={mileage}
                    unit="km"
                />
                <BatteryHistoryGraphTooltipKpi
                    i18nId="intl-msg:batteryService.batteryAge"
                    value={usage_days}
                    formatter={(value) => getDaysInYearsAndMonths(value, intl)}
                />
                <BatteryHistoryGraphTooltipKpi
                    i18nId="intl-msg:batteryService.dischargedEnergyThroughput"
                    value={discharging_energy_throughput}
                    unit="kWh"
                />
                <BatteryHistoryGraphTooltipKpi
                    i18nId="intl-msg:batteryService.fullChargeEnergy"
                    value={full_charge_energy}
                    unit="kWh"
                />
                <BatteryHistoryGraphTooltipKpi
                    i18nId="intl-msg:batteryService.depthOfDischarge"
                    value={depth_of_discharge_percentage}
                    unit="%"
                />
                {isCbpVehicle && (
                    <BatteryHistoryGraphTooltipKpi
                        i18nId="intl-msg:batteryService.chargedEnergyFastCharge"
                        value={charged_energy_fast_charge}
                        unit="kWh"
                    />
                )}
            </div>
        );
    }
    return null;
};
